import { Box, Button, Divider, Typography } from '@mui/material';
import { useI18n } from 'AurionCR/components';
import getUserDetailsOptionLabel from 'AurionCR/components/form/user-details-select/get-option-label';
import renderUserDetailsOption from 'AurionCR/components/form/user-details-select/render-option/render-user-details-option';
import { IUserDetailsItem } from 'AurionCR/components/form/user-details-select/types';
import { FormController, Input, Select } from 'AurionCR/components/formV2';
import { FileDisplay, FileUploader } from 'components/form';
import { DateTimePicker } from 'components/form/date-picker-separate-time';
import { REPORT_FILE_TYPE } from 'components/helpers';
import { useStepHooks } from 'pages/CreditRatings/RatingProcess/components/editor/components/step-hooks';
import {
  IRPStepDoneForm,
  RPStepSubmitForm,
} from 'pages/CreditRatings/RatingProcess/components/editor/store';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { API_DOCUMENTATION_STATUS } from 'services/documentation-status/models';
import { API_USER_DETAIL } from 'services/user-detail';
import { useAppDispatch } from 'store';
import style from './index.module.scss';
import { prepareData } from './step-done';

const DOCUMENTATION_STATUS_SOURCE = `${API_DOCUMENTATION_STATUS.GET_ALL_DYNAMIC}?Select=id,title, isActive&OrderBy=isActive desc,rank`;

export const StepDonePreviewForm = memo(
  ({ data, loading }: { data: prepareData | null; loading: boolean }) => {
    const { t } = useI18n();

    const {
      control,

      handleSubmit,
      setValue,
      watch,
    } = useForm<IRPStepDoneForm>({
      defaultValues: {
        monitorVotingSheetFilePath: data?.monitorVotingSheetFilePath,
        monitorVotingSheetLinkToFile: data?.monitorVotingSheetLinkToFile,
        monitorFinancialAnalysisFilePath: data?.monitorVotingSheetFilePath,
        monitorFinancialAnalysisLinkToFile: data?.monitorFinancialAnalysisLinkToFile,
        monitorOtherFilePath: data?.monitorOtherFilePath,
        monitorOtherLinkToFile: data?.monitorOtherLinkToFile,
        monitorDescription: data?.monitorDescription,
        monitorUserID: data?.monitorUserID,
        monitorSignatureDate: data?.monitorSignatureDate,
        monitorApproval1DocumentationStatusID: data?.monitorApproval1DocumentationStatusID,
        monitorApproval2DocumentationStatusID: data?.monitorApproval2DocumentationStatusID,
        monitorApproval3DocumentationStatusID: data?.monitorApproval3DocumentationStatusID,
        monitorApproval4DocumentationStatusID: data?.monitorApproval4DocumentationStatusID,
      },
    });
    const dispatch = useAppDispatch();
    const { isAllowToEditRatingProcess } = useStepHooks();

    const handleSave = useCallback(
      (data: IRPStepDoneForm) => {
        if (isAllowToEditRatingProcess) {
          dispatch(RPStepSubmitForm(data));
        }
      },
      [dispatch, isAllowToEditRatingProcess],
    );

    const onSubmit = useCallback(() => {
      handleSubmit(handleSave)();
    }, [handleSave, handleSubmit]);

    const monitorVotingSheetFilePath = watch('monitorVotingSheetFilePath');
    const monitorFinancialAnalysisFilePath = watch('monitorFinancialAnalysisFilePath');
    const monitorOtherFilePath = watch('monitorOtherFilePath');
    const monitorDescription = watch('monitorDescription');
    const monitorVotingSheetLinkToFile = watch('monitorVotingSheetLinkToFile');
    const monitorFinancialAnalysisLinkToFile = watch('monitorFinancialAnalysisLinkToFile');

    // File removal handlers
    const handleRemoveVotingSheetFile = useCallback(() => {
      setValue('monitorVotingSheetFilePath', '');
    }, [setValue]);

    const handleRemoveFinancialAnalysisFile = useCallback(() => {
      setValue('monitorFinancialAnalysisFilePath', '');
    }, [setValue]);

    const handleRemoveOtherFile = useCallback(() => {
      setValue('monitorOtherFilePath', '');
    }, [setValue]);

    //render
    return (
      <>
        <tr>
          <td>
            {(monitorVotingSheetFilePath ||
              monitorFinancialAnalysisFilePath ||
              monitorOtherFilePath) && (
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#666',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  {t('previous-file-version')}
                </Typography>
                {monitorVotingSheetFilePath && (
                  <FileDisplay
                    value={monitorVotingSheetFilePath}
                    label={t('monitor-voting-sheet-file')}
                    disabled={loading}
                    onRemove={handleRemoveVotingSheetFile}
                  />
                )}
                {monitorFinancialAnalysisFilePath && (
                  <FileDisplay
                    value={monitorFinancialAnalysisFilePath}
                    label={t('monitor-financial-analysis-file')}
                    disabled={loading}
                    onRemove={handleRemoveFinancialAnalysisFile}
                  />
                )}
                {monitorOtherFilePath && (
                  <FileDisplay
                    value={monitorOtherFilePath}
                    label={t('monitor-other-file-path')}
                    disabled={loading}
                    onRemove={handleRemoveOtherFile}
                  />
                )}
              </Box>
            )}
          </td>
        </tr>
        <tr>
          <FormController
            name="monitorVotingSheetFilePath"
            as={FileUploader}
            control={control}
            disabled={loading}
            fileAccept={REPORT_FILE_TYPE}
            renderProps={{
              label: t('monitor-voting-sheet-file'),
              disabled: loading,
            }}
            rules={{
              validate: () =>
                monitorVotingSheetLinkToFile || monitorDescription
                  ? true
                  : t('required-two-files-or-description'),
            }}
            className={'FormControllerStyled'}
            fileNamePrefixTemplate={t('monitor-voting-sheet-file')}
          />
        </tr>
        <tr>
          <FormController
            name="monitorFinancialAnalysisFilePath"
            as={FileUploader}
            control={control}
            disabled={loading}
            fileAccept={REPORT_FILE_TYPE}
            rules={{
              validate: () =>
                monitorFinancialAnalysisLinkToFile || monitorDescription
                  ? true
                  : t('required-two-files-or-description'),
            }}
            renderProps={{
              label: t('monitor-financial-analysis-file'),
              disabled: loading,
            }}
            className={'FormControllerStyled'}
            fileNamePrefixTemplate={t('monitor-financial-analysis-file')}
          />
        </tr>
        <tr>
          <FormController
            name="monitorOtherFilePath"
            as={FileUploader}
            control={control}
            disabled={loading}
            fileAccept={REPORT_FILE_TYPE}
            renderProps={{
              label: t('monitor-other-file-path'),
              disabled: loading,
            }}
            className={'FormControllerStyled'}
            fileNamePrefixTemplate={t('monitor-other-file-path')}
          />
        </tr>
        <tr>
          <FormController
            name="monitorDescription"
            label={t('monitor-description')}
            as={Input}
            rules={{
              validate: () =>
                monitorDescription ||
                (monitorVotingSheetLinkToFile && monitorFinancialAnalysisLinkToFile)
                  ? true
                  : t('required-two-files-or-description'),
            }}
            disabled={loading}
            control={control}
            className={'FormControllerStyled'}
            multiline
            minRows={3}
            maxRows={5}
          />
        </tr>
        <tr>
          <FormController
            name="monitorUserID"
            placeholder={t('monitor-user')}
            label={t('monitor-user')}
            as={Select}
            rules={'required'}
            control={control}
            disabled={loading}
            source={`${API_USER_DETAIL.GET_ALL_DYNAMIC}?Select=userID as id,fullName as title, isActive&Filter=isSystemUser==true&%26%26&OrderBy=isActive desc,firstName asc,lastName asc`}
            className={style.analyst}
            renderOption={renderUserDetailsOption}
            getOptionLabel={(item: IUserDetailsItem) => getUserDetailsOptionLabel({ ...item, t })}
          />
        </tr>
        <tr>
          <FormController
            name="monitorApproval1DocumentationStatusID"
            placeholder={t('monitor-approval1-documentation-status')}
            label={t('monitor-approval1-documentation-status')}
            as={Select}
            rules={'required'}
            control={control}
            disabled={loading}
            source={DOCUMENTATION_STATUS_SOURCE}
            className={style.analyst}
            renderOption={renderUserDetailsOption}
            getOptionLabel={(item: IUserDetailsItem) => getUserDetailsOptionLabel({ ...item, t })}
          />
        </tr>
        <tr>
          <FormController
            name="monitorApproval2DocumentationStatusID"
            placeholder={t('monitor-approval2-documentation-status')}
            label={t('monitor-approval2-documentation-status')}
            as={Select}
            rules={'required'}
            control={control}
            disabled={loading}
            source={DOCUMENTATION_STATUS_SOURCE}
            className={style.analyst}
            renderOption={renderUserDetailsOption}
            getOptionLabel={(item: IUserDetailsItem) => getUserDetailsOptionLabel({ ...item, t })}
          />
        </tr>
        <tr>
          <FormController
            name="monitorApproval3DocumentationStatusID"
            placeholder={t('monitor-approval3-documentation-status')}
            label={t('monitor-approval3-documentation-status')}
            as={Select}
            rules={'required'}
            control={control}
            disabled={loading}
            source={DOCUMENTATION_STATUS_SOURCE}
            className={style.analyst}
            renderOption={renderUserDetailsOption}
            getOptionLabel={(item: IUserDetailsItem) => getUserDetailsOptionLabel({ ...item, t })}
          />
        </tr>
        <tr>
          <FormController
            name="monitorApproval4DocumentationStatusID"
            placeholder={t('monitor-approval4-documentation-status')}
            label={t('monitor-approval4-documentation-status')}
            as={Select}
            rules={'required'}
            control={control}
            disabled={loading}
            source={DOCUMENTATION_STATUS_SOURCE}
            className={style.analyst}
            renderOption={renderUserDetailsOption}
            getOptionLabel={(item: IUserDetailsItem) => getUserDetailsOptionLabel({ ...item, t })}
          />
        </tr>
        <tr>
          <FormController
            rules={'date,required'}
            name="monitorSignatureDate"
            label={t('monitor-signature-date')}
            as={DateTimePicker}
            format={'dd/MM/yyyy'}
            control={control}
            disabled={loading}
          />
        </tr>
        <Box display={'flex'} justifyContent={'end'} mb={'1rem'}>
          <Button
            className={style.btnFinish}
            color="primary"
            variant="contained"
            disabled={loading}
            onClick={onSubmit}
          >
            {t('save')}
          </Button>
        </Box>
        <Divider orientation={'horizontal'} />
      </>
    );
  },
);
